import throttle from 'lodash/throttle'

# Setup Window Resize Handler
#
# Setup a throttled function that runs when the window is resized.  Pass in an optional media query to run only in a certain media query range.
#
# Params:
#   resizeHandler: Your resize handler (function)
#   mediaQueryArg: Your media query (string, optional).  Ex: '(min-width: 768px)'
#   throttleTime: Amount to throttle your handler (milliseconds, optional)
#   onCleanup: Optional cleanup function that runs when your media query stops matching or when your component is destroyed.
#
# Usage:
#   mounted: -> @cleanupResize = setupWindowResizeHandler @myResizeHandler, "(min-width: 1024px)", 300, @myCleanupHandler
#   beforeDestroy: -> @cleanupResize()

export setupWindowResizeHandler = (resizeHandler, mediaQueryArg=null, throttleTime=300, onCleanup=null) ->
	# We need this because throttle occasionally runs our handler after motionCleanup and onCleanup
	isActive = false

	throttledResizeHandler = throttle ->
		resizeHandler() if isActive
	, throttleTime

	motionSetup = ->
		# On media query match run immediately.
		# Delay 1 tick in case we have to wait for child components to render.  SetTimeout was more reliable than $nextTick.
		setTimeout =>
			resizeHandler()
		, 0
		# Add event listener
		window?.addEventListener 'resize', throttledResizeHandler
		isActive = true

	motionCleanup = ->
		window?.removeEventListener 'resize', throttledResizeHandler
		onCleanup?()
		isActive = false

	if !mediaQueryArg
		# If no media query, then just start listening.
		motionSetup()
	else
		# If media query, then run motionSetup() when we match, and run motionCleanup() when we don't match.
		mediaQuery = window?.matchMedia mediaQueryArg
		mqListener = (mq) -> if mq.matches then motionSetup() else motionCleanup()
		mediaQuery.addListener mqListener
		# Bind listeners. This will also run motionSetup now if we match.
		mqListener mediaQuery

	# Return cleanup function
	return () ->
		mediaQuery?.removeListener mqListener
		motionCleanup()


# IsOverflowing
# Check if an element is overflowing its bounds.
# Temporarily sets overflow:hidden if overflow is visible.
# Second param: 'horizontal', 'vertical', 'both'
# https://stackoverflow.com/questions/143815/determine-if-an-html-elements-content-overflows
export isOverflowing = (el, mode='horizontal') ->
	curOverflow = el.style.overflow
	if !curOverflow or curOverflow == "visible"
		el.style.overflow = "hidden"
	if mode == 'horizontal'
		result = el.clientWidth < el.scrollWidth
	else if mode == 'vertical'
		result = el.clientHeight < el.scrollHeight
	else if mode == 'both'
		result = el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight
	else
		console.warn 'isOverflowing: second param must be "horizontal", "vertical", or "both".'
	el.style.overflow = curOverflow
	return result
